/**
 * ChatWidget - Real-time chat between customer and provider
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  MessageCircle, X, Send, Loader2, ChevronDown, User, Store
} from 'lucide-react';
import { format, parseISO, isToday, isYesterday } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chat bubble component
function ChatBubble({ message, isOwn }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn
              ? 'bg-purple-600 text-white rounded-br-sm'
              : 'bg-gray-100 text-gray-800 rounded-bl-sm'
          }`}
        >
          <p className="text-sm">{message.content}</p>
        </div>
        <p className={`text-xs text-gray-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
          {format(parseISO(message.created_at), 'h:mm a')}
        </p>
      </div>
    </div>
  );
}

// Date separator
function DateSeparator({ date }) {
  const dateObj = parseISO(date);
  let label = format(dateObj, 'MMMM d, yyyy');
  if (isToday(dateObj)) label = 'Today';
  if (isYesterday(dateObj)) label = 'Yesterday';

  return (
    <div className="flex items-center gap-2 my-4">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-xs text-gray-400 px-2">{label}</span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

export default function ChatWidget({ 
  providerId, 
  providerName, 
  bookingId = null,
  customerName = 'Customer',
  customerEmail = null,
  userType = 'customer'  // customer or provider
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize or get conversation
  useEffect(() => {
    if (isOpen && !conversation) {
      initConversation();
    }
  }, [isOpen]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // WebSocket connection
  useEffect(() => {
    if (conversation?.id) {
      connectWebSocket();
      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [conversation?.id]);

  const connectWebSocket = () => {
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/api/chat/ws/${conversation.id}`);

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_message') {
        setMessages(prev => [...prev, data.message]);
        if (data.message.sender_type !== userType) {
          setUnreadCount(prev => prev + 1);
        }
      }
    };

    wsRef.current.onerror = () => {
      console.log('WebSocket error, falling back to polling');
    };
  };

  const initConversation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/event-conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          provider_id: providerId,
          customer_name: customerName,
          customer_email: customerEmail,
          subject: `Chat with ${providerName}`
        })
      });

      if (response.ok) {
        const conv = await response.json();
        setConversation(conv);
        
        // Fetch messages
        const msgRes = await fetch(`${API_URL}/api/chat/event-conversations/${conv.id}`);
        if (msgRes.ok) {
          const data = await msgRes.json();
          setMessages(data.messages || []);
        }
      }
    } catch (error) {
      console.error('Failed to init conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      // If WebSocket is connected, send through it
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'message',
          sender_type: userType,
          sender_name: userType === 'customer' ? customerName : providerName,
          content: newMessage
        }));
      } else {
        // Fallback to REST API
        await fetch(`${API_URL}/api/chat/event-messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversation_id: conversation.id,
            sender_type: userType,
            sender_name: userType === 'customer' ? customerName : providerName,
            content: newMessage
          })
        });
        
        // Refresh messages
        const msgRes = await fetch(`${API_URL}/api/chat/event-conversations/${conversation.id}`);
        if (msgRes.ok) {
          const data = await msgRes.json();
          setMessages(data.messages || []);
        }
      }
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  // Floating chat button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 p-4 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all hover:scale-105 z-50"
        data-testid="chat-widget-btn"
      >
        <MessageCircle className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-xs rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 z-50 shadow-2xl rounded-lg overflow-hidden" data-testid="chat-widget">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Store className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">{providerName}</h3>
              <p className="text-xs text-purple-200">Usually replies within an hour</p>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white h-80 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-400 mt-8">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Start a conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <ChatBubble
                key={msg.id || idx}
                message={msg}
                isOwn={msg.sender_type === userType}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="bg-gray-50 p-3 border-t">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1"
            disabled={!conversation}
          />
          <Button 
            type="submit" 
            disabled={!newMessage.trim() || sending || !conversation}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
